import { defineStore } from 'pinia'

import {
  type ListExportParams,
  type StartExportParams,
  deleteExport,
  listExportSources,
  listExports,
  startExport,
} from '@/api'
import { errorParser } from '@/helpers'
import type { PageNumberPagination, UUID } from '@/types'
import type { CorpusExport, ExportSource } from '@/types/export'

import { useJobsStore, useNotificationStore } from '.'

interface State {
  /**
   * Exports by corpora.
   */
  corpusExports: { [corpusId: UUID]: PageNumberPagination<CorpusExport> }

  /**
   * Available export sources. Arkindex EE only.
   */
  sources: ExportSource[] | null
}

export const useExportStore = defineStore('exports', {
  state: (): State => ({
    corpusExports: {},
    sources: null,
  }),
  actions: {
    async list(corpusId: UUID, page = 1, datasetId: UUID | null = null) {
      const params: ListExportParams = { page }
      if (datasetId) params.dataset_id = datasetId
      try {
        const data = await listExports(corpusId, params)
        this.corpusExports[corpusId] = data
      } catch (err) {
        useNotificationStore().notify({ type: 'error', text: errorParser(err) })
      }
    },
    async start(corpusId: UUID, source: string = 'default', datasetId: UUID | null = null) {
      const params: StartExportParams = { source }
      if (datasetId) params.dataset_id = datasetId
      try {
        await startExport(corpusId, params)
        useJobsStore().list()
      } catch (err) {
        useNotificationStore().notify({ type: 'error', text: errorParser(err) })
      }
    },
    async delete(id: UUID, corpusId: UUID) {
      try {
        await deleteExport(id)
        this.list(corpusId)
      } catch (err) {
        useNotificationStore().notify({ type: 'error', text: errorParser(err) })
      }
    },
    async listSources() {
      if (Array.isArray(this.sources)) return
      try {
        this.sources = await listExportSources()
      } catch (err) {
        useNotificationStore().notify({ type: 'error', text: errorParser(err) })
      }
    },
  },
})
